import React from 'react';
import {
  Box,
  Typography,
  Checkbox,
  FormControlLabel,
  Stack,
  Button
} from '@mui/material';

import TreeView from '@mui/lab/TreeView';
import TreeItem from '@mui/lab/TreeItem';

import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';

import { SmallFormDateField } from '../../../components/small-fields/SmallFormDateField';
import { formatDate } from '../../../services/functions';

import { useTfg } from './TfgContext';

const CheckboxTreeItem = ({
  nodeId,
  label,
  name,
  values,
  selected,
  onChange,
  onChangeAll
}) => (
  <TreeItem
    nodeId={nodeId}
    label={
      <FormControlLabel
        label={label}
        control={
          <Checkbox
            checked={
              selected.length === values?.length &&
              values?.length > 0
            }
            indeterminate={
              selected.length > 0 &&
              selected.length < values?.length
            }
            onClick={e => e.stopPropagation()}
            onChange={e => onChangeAll(e, name)}
          />
        }
      />
    }
  >
    <Stack sx={{ pl: 2 }}>
      {values?.map(item => (
        <FormControlLabel
          key={item.value}
          label={item.label}
          control={
            <Checkbox
              checked={selected.includes(item.value)}
              onChange={() => onChange(item.value, name)}
            />
          }
        />
      ))}
    </Stack>
  </TreeItem>
);

const DateRangeTreeItem = ({
  nodeId,
  label,
  fromName,
  toName,
  filters,
  setFilters
}) => (
  <TreeItem
    nodeId={nodeId}
    label={<Typography sx={{ p: 1 }}>{label}</Typography>}
  >
    <SmallFormDateField
      sx={{ width: '100%', mb: 2 }}
      name={fromName}
      label="Od"
      variant="standard"
      value={filters[fromName]}
      onChange={val => setFilters(prev => ({
        ...prev,
        [fromName]: formatDate(val)
      }))
      }
    />

    <SmallFormDateField
      sx={{ width: '100%', mb: 2 }}
      name={toName}
      label="Do"
      variant="standard"
      value={filters[toName]}
      onChange={val => setFilters(prev => ({
        ...prev,
        [toName]: formatDate(val)
      }))
      }
    />
  </TreeItem>
);

export default function TfgFilter() {
  const { setFiltersParams, choices } = useTfg();

  const [filters, setFilters] = React.useState({
    status: [],
    operation_type: [],
    correction_reason: [],
    error_notification_sent: false,

    created_at_from: null,
    created_at_to: null,

    scheduled_at_from: null,
    scheduled_at_to: null,

    deadline_at_from: null,
    deadline_at_to: null,

    processed_at_from: null,
    processed_at_to: null
  });

  const resetFilters = () => setFilters({
    status: [],
    operation_type: [],
    correction_reason: [],
    error_notification_sent: false,

    created_at_from: null,
    created_at_to: null,

    scheduled_at_from: null,
    scheduled_at_to: null,

    deadline_at_from: null,
    deadline_at_to: null,

    processed_at_from: null,
    processed_at_to: null
  });

  React.useEffect(() => {
    const params = new URLSearchParams();

    if (filters.status.length) {
      params.append(
        'status__in',
        filters.status.join(',')
      );
    }

    if (filters.operation_type.length) {
      params.append(
        'operation_type__in',
        filters.operation_type.join(',')
      );
    }

    if (filters.correction_reason.length) {
      params.append(
        'correction_reason__in',
        filters.correction_reason.join(',')
      );
    }

    Object.entries(filters).forEach(([key, value]) => {
      if (
        (key.endsWith('_from') || key.endsWith('_to')) &&
        value
      ) {
        params.append(key, value);
      }
    });

    if (filters.error_notification_sent) {
      params.append(
        'error_notification_sent',
        true
      );
    }

    setFiltersParams(params.toString());
  }, [filters]);

  const handleChangeArray = (value, name) => {
    if (filters[name].includes(value)) {
      setFilters(prev => ({
        ...prev,
        [name]: prev[name].filter(v => v !== value)
      }));
    } else {
      setFilters(prev => ({
        ...prev,
        [name]: [...prev[name], value]
      }));
    }
  };

  const handleChangeArrayAll = (e, name) => {
    setFilters(prev => ({
      ...prev,
      [name]: e.target.checked
        ? choices[name]?.map(item => item.value) ?? []
        : []
    }));
  };

  return (
    <Box sx={{ width: '95%' }}>
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="baseline"
      >
        <Typography variant="h6" sx={{ mb: 2 }}>
          Filtry
        </Typography>

        <Button onClick={resetFilters}>
          Resetuj
        </Button>
      </Stack>

      <TreeView
        defaultExpanded={['1', '2']}
        defaultCollapseIcon={<ExpandMoreIcon />}
        defaultExpandIcon={<ChevronRightIcon />}
      >
        <CheckboxTreeItem
          nodeId="1"
          label="Status"
          name="status"
          values={choices.status}
          selected={filters.status}
          onChange={handleChangeArray}
          onChangeAll={handleChangeArrayAll}
        />

        <CheckboxTreeItem
          nodeId="2"
          label="Operacja"
          name="operation_type"
          values={choices.operation_type}
          selected={filters.operation_type}
          onChange={handleChangeArray}
          onChangeAll={handleChangeArrayAll}
        />

        <CheckboxTreeItem
          nodeId="3"
          label="Powód korekty"
          name="correction_reason"
          values={choices.correction_reason}
          selected={filters.correction_reason}
          onChange={handleChangeArray}
          onChangeAll={handleChangeArrayAll}
        />

        {/* <DateRangeTreeItem
          nodeId="4"
          label="Zaplanowano"
          fromName="scheduled_at_from"
          toName="scheduled_at_to"
          filters={filters}
          setFilters={setFilters}
        /> */}

        <DateRangeTreeItem
          nodeId="5"
          label="Deadline"
          fromName="deadline_at_from"
          toName="deadline_at_to"
          filters={filters}
          setFilters={setFilters}
        />

        <DateRangeTreeItem
          nodeId="6"
          label="Przetworzono"
          fromName="processed_at_from"
          toName="processed_at_to"
          filters={filters}
          setFilters={setFilters}
        />

        <DateRangeTreeItem
          nodeId="7"
          label="Utworzono"
          fromName="created_at_from"
          toName="created_at_to"
          filters={filters}
          setFilters={setFilters}
        />

        {/* <TreeItem
          sx={{ mt: 1 }}
          nodeId="8"
          label={
            <FormControlLabel
              label="Powiadomienie o błędzie wysłane"
              control={
                <Checkbox
                  checked={filters.error_notification_sent}
                  onChange={e => setFilters(prev => ({
                    ...prev,
                    error_notification_sent: e.target.checked
                  }))
                  }
                />
              }
            />
          }
        /> */}
      </TreeView>
    </Box>
  );
}
